import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import {
  getRequestOptions,
  postRequestOptions,
  putRequestOptions,
} from '../../utils/utils';
import { PreventiveMaintenanceRecord } from '../../Interfaces/Maintenance/PreventiveMaintenanceRecord';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { PredictiveMaintenanceRecord } from '../../Interfaces/Maintenance/PredictiveMaintenanceRecord';
import { FetchError } from '../../Interfaces/FetchError';

export enum MaintenanceActionTypes {
  RECEIVED_OPEN_PREV_MAINTENANCE_RECORDS = 'RECEIVED_OPEN_PREV_MAINTENANCE_RECORDS',
  RECEIVED_ALL_PREV_MAINTENANCE_RECORDS = 'RECEIVED_ALL_PREV_MAINTENANCE_RECORDS',
  UPDATE_PREV_MAINTENANCE_RECORD = 'UPDATE_PREV_MAINTENANCE_RECORD',
  RECEIVED_ALL_PRED_MAINTENANCE_RECORDS = 'RECEIVED_ALL_PRED_MAINTENANCE_RECORDS',
  UPDATE_PRED_MAINTENANCE_RECORD = 'UPDATE_PRED_MAINTENANCE_RECORD',
  MAINTENANCE_UNAUTHORIZED_REQUEST = 'MAINTENANCE_UNAUTHORIZED_REQUEST',
  RECEIVED_UPDATE_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_UPDATE_HTTP_RESPONSE_ERROR_CODE',
  TRANSFER_SERVICE_PING = 'TRANSFER_SERVICE_PING',
  RESET_HTTP_RESPONSE_FETCH_ERROR = 'RESET_HTTP_RESPONSE_FETCH_ERROR',
  PING_HTTP_RESPONSE_ERROR_CODE = 'PING_HTTP_RESPONSE_ERROR_CODE',
  RECEIVED_PREVENTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_PREVENTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE',
  RECEIVED_PREDICTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE = 'RECEIVED_PREDICTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE',
}

export interface MaintenanceState {
  prevMaintenanceRecords: PreventiveMaintenanceRecord[];
  predMaintenanceRecords: PredictiveMaintenanceRecord[];
  fetchError: FetchError;
}

interface GetPrevMaintenanceRecords {
  type: MaintenanceActionTypes.RECEIVED_ALL_PREV_MAINTENANCE_RECORDS;
  records: PreventiveMaintenanceRecord[];
}

interface UpdatePrevMaintenanceRecord {
  type: MaintenanceActionTypes.UPDATE_PREV_MAINTENANCE_RECORD;
  record: PreventiveMaintenanceRecord;
}

interface GetPredMaintenanceRecords {
  type: MaintenanceActionTypes.RECEIVED_ALL_PRED_MAINTENANCE_RECORDS;
  records: PredictiveMaintenanceRecord[];
}

interface UpdatePredMaintenanceRecord {
  type: MaintenanceActionTypes.UPDATE_PRED_MAINTENANCE_RECORD;
  record: PredictiveMaintenanceRecord;
}

interface TransferServicePing {
  type: MaintenanceActionTypes.TRANSFER_SERVICE_PING;
}

interface ReceivedUpdateErrorCode {
  type: MaintenanceActionTypes.RECEIVED_UPDATE_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ReceivedPreventiveRecordsErrorCode {
  type: MaintenanceActionTypes.RECEIVED_PREVENTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ReceivedPredictiveRecordsErrorCode {
  type: MaintenanceActionTypes.RECEIVED_PREDICTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface PingErrorCode {
  type: MaintenanceActionTypes.PING_HTTP_RESPONSE_ERROR_CODE;
  statusCode: number;
}

interface ResetHttpResponseError {
  type: MaintenanceActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR;
}

type KnownAction =
  | GetPrevMaintenanceRecords
  | UpdatePrevMaintenanceRecord
  | GetPredMaintenanceRecords
  | UpdatePredMaintenanceRecord
  | TransferServicePing
  | ReceivedUpdateErrorCode
  | PingErrorCode
  | ReceivedPreventiveRecordsErrorCode
  | ReceivedPredictiveRecordsErrorCode
  | ResetHttpResponseError;

const isResponseOk = (
  response: Response,
  dispatch: any,
  message: MaintenanceActionTypes
): boolean => {
  if (response.ok) return true;

  const statusCode = response.status;
  if (statusCode === 401) {
    dispatch({
      type: MaintenanceActionTypes.MAINTENANCE_UNAUTHORIZED_REQUEST,
    });
  } else {
    dispatch({
      type: message,
      statusCode: statusCode,
    });
  }

  return false;
};

export const actionCreators = {
  fetchPrevMaintenanceRecords:
    (includecompleted: boolean): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `Maintenance/GetPreventive/${includecompleted}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          appState.userSettingsReducer.prevMaintenanceFilter
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (
        !isResponseOk(
          response,
          dispatch,
          MaintenanceActionTypes.RECEIVED_PREVENTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE
        )
      )
        return;

      const result = (await response.json()) as PreventiveMaintenanceRecord[];

      if (includecompleted) {
        console.log(
          `Dispatching ${MaintenanceActionTypes.RECEIVED_ALL_PREV_MAINTENANCE_RECORDS}`
        );
      } else {
        console.log(
          `Dispatching ${MaintenanceActionTypes.RECEIVED_OPEN_PREV_MAINTENANCE_RECORDS}`
        );
      }
      dispatch({
        type: MaintenanceActionTypes.RECEIVED_ALL_PREV_MAINTENANCE_RECORDS,
        records: result,
      });
    },

  updatePrevMaintenanceRecord:
    (
      record: PreventiveMaintenanceRecord,
      newStatus: ApiEnums.PreventiveMaintenanceStatus
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${MaintenanceActionTypes.UPDATE_PREV_MAINTENANCE_RECORD}`
      );

      const response = await fetch(
        `Maintenance/UpdatePreventive/${record.ID}/${newStatus}`,
        putRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });
      if (
        !isResponseOk(
          response,
          dispatch,
          MaintenanceActionTypes.RECEIVED_UPDATE_HTTP_RESPONSE_ERROR_CODE
        )
      )
        return;
      const result = (await response.json()) as PreventiveMaintenanceRecord;

      dispatch({
        type: MaintenanceActionTypes.UPDATE_PREV_MAINTENANCE_RECORD,
        record: result,
      });
    },

  fetchPredMaintenanceRecords:
    (includeCompleted: boolean): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `Maintenance/GetPredictive/${includeCompleted}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          appState.userSettingsReducer.predMaintenanceFilter
        )
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (
        !isResponseOk(
          response,
          dispatch,
          MaintenanceActionTypes.RECEIVED_PREDICTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE
        )
      )
        return;

      const result = (await response.json()) as PredictiveMaintenanceRecord[];

      console.log(
        `Dispatching ${MaintenanceActionTypes.RECEIVED_ALL_PRED_MAINTENANCE_RECORDS}`
      );

      dispatch({
        type: MaintenanceActionTypes.RECEIVED_ALL_PRED_MAINTENANCE_RECORDS,
        records: result,
      });
    },

  updatePredMaintenanceRecord:
    (
      record: PredictiveMaintenanceRecord,
      newStatus: ApiEnums.PredictiveMaintenanceStatus
    ): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${MaintenanceActionTypes.UPDATE_PRED_MAINTENANCE_RECORD}`
      );

      const response = await fetch(
        `Maintenance/UpdatePredictive/${record.ID}/${newStatus}`,
        putRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (
        !isResponseOk(
          response,
          dispatch,
          MaintenanceActionTypes.RECEIVED_UPDATE_HTTP_RESPONSE_ERROR_CODE
        )
      )
        return;
      record = (await response.json()) as PredictiveMaintenanceRecord;

      dispatch({
        type: MaintenanceActionTypes.UPDATE_PRED_MAINTENANCE_RECORD,
        record: record,
      });
    },

  pingDMMTransferService:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const response = await fetch(
        `Maintenance/Ping`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });
      if (
        !isResponseOk(
          response,
          dispatch,
          MaintenanceActionTypes.PING_HTTP_RESPONSE_ERROR_CODE
        )
      )
        return;

      console.log(
        `Dispatching ${MaintenanceActionTypes.TRANSFER_SERVICE_PING}`
      );

      dispatch({
        type: MaintenanceActionTypes.TRANSFER_SERVICE_PING,
      });
    },

  resetFetchError:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(
        `Dispatching ${MaintenanceActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR}`
      );

      dispatch({
        type: MaintenanceActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR,
      });
    },
};

export const unloadedState: MaintenanceState = {
  prevMaintenanceRecords: [],
  predMaintenanceRecords: [],
  fetchError: { StatusCode: 0, Message: '' },
};

export const reducer: Reducer<MaintenanceState> = (
  state: MaintenanceState | undefined,
  incomingAction: Action
): MaintenanceState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case MaintenanceActionTypes.RECEIVED_ALL_PREV_MAINTENANCE_RECORDS:
      return {
        ...state,
        prevMaintenanceRecords: action.records,
      };
    case MaintenanceActionTypes.UPDATE_PREV_MAINTENANCE_RECORD:
      const newState: MaintenanceState = {
        ...state,
        prevMaintenanceRecords: [...state.prevMaintenanceRecords],
      };

      let index = newState.prevMaintenanceRecords.findIndex(
        ({ ID }) => ID === action.record.ID
      );

      if (index >= 0) {
        newState.prevMaintenanceRecords[index] = {
          ...newState.prevMaintenanceRecords[index],
          ...action.record,
        };
      }

      return newState;
    case MaintenanceActionTypes.RECEIVED_ALL_PRED_MAINTENANCE_RECORDS:
      return {
        ...state,
        predMaintenanceRecords: action.records,
      };
    case MaintenanceActionTypes.UPDATE_PRED_MAINTENANCE_RECORD:
      const newPredState: MaintenanceState = {
        ...state,
        predMaintenanceRecords: [...state.predMaintenanceRecords],
      };

      let recordIndex = newPredState.predMaintenanceRecords.findIndex(
        ({ ID }) => ID === action.record.ID
      );

      if (recordIndex >= 0) {
        newPredState.predMaintenanceRecords[recordIndex] = {
          ...newPredState.predMaintenanceRecords[recordIndex],
          ...action.record,
        };
      }

      return newPredState;

    case MaintenanceActionTypes.RESET_HTTP_RESPONSE_FETCH_ERROR:
      let resetData = { StatusCode: 0, Message: '' };
      return {
        ...state,
        fetchError: resetData,
      };
    case MaintenanceActionTypes.RECEIVED_UPDATE_HTTP_RESPONSE_ERROR_CODE:
      let updateErrorData = {
        StatusCode: action.statusCode,
        Message: 'UPDATE_ERROR_MESSAGE_MAINTENANCE',
      };
      return {
        ...state,
        fetchError: updateErrorData,
      };
    case MaintenanceActionTypes.PING_HTTP_RESPONSE_ERROR_CODE:
      let pingErrorData = {
        StatusCode: action.statusCode,
        Message: 'PING_ERROR_MESSAGE_MAINTENANCE',
      };
      return {
        ...state,
        fetchError: pingErrorData,
      };
    case MaintenanceActionTypes.RECEIVED_PREVENTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE:
      let fetchPreventiveErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_PREVENTIVE_ERROR_MESSAGE_MAINTENANCE',
      };
      return {
        ...state,
        fetchError: fetchPreventiveErrorData,
      };
    case MaintenanceActionTypes.RECEIVED_PREDICTIVE_RECORDS_HTTP_RESPONSE_ERROR_CODE:
      let fetchPredictiveErrorData = {
        StatusCode: action.statusCode,
        Message: 'FETCH_PREDICTIVE_ERROR_MESSAGE_MAINTENANCE',
      };
      return {
        ...state,
        fetchError: fetchPredictiveErrorData,
      };
    default:
      break;
  }
  return state;
};
